import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import config from "../common/Config";
import generatePickerListPDF from "./PickerListPDF";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import Swal from "sweetalert2";
import { Iconify } from "src/components/iconify";
import "../common/commonStyles.css";

const SingleViewPickinglist = () => {
  const { picklistNo } = useParams();
  const accessToken = localStorage.getItem("accessToken");
  const firstName = localStorage.getItem("first_name");
  const lastName = localStorage.getItem("last_name");
  const [showCustomer, setShowCustomer] = useState(false);
  const todayDate = new Date();

  function formatDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function formatNewDate(dateString) {
    if (dateString == "0000-00-00") {
      return "";
    }
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const [headerData, setHeaderData] = useState({
    customerName: "",
    orderNumber: "",
    accountNumber: "",
    routeNumber: "",
    pickingList: "",
    deliveryDate: "",
    printBy: "",
    printDate: "",
    shop_name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
  });
  const [pickList, setPickList] = useState([]);
  const [pickComment, setPickComment] = useState([]);
  const [totalQty, setTotalQty] = useState(0);

  const handleGeneratePickerListPDF = () => {
    generatePickerListPDF(picklistNo);
  };

  const fetchViewPickerList = async () => {
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = { authorization: `Bearer ${accessToken}` };
      let body;
      if (useEncrypt) {
        const ep = encryptPayload({ picklist_number: picklistNo });
        if (!ep) {
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        body = fd;
      } else {
        const formData = new FormData();
        formData.set("picklist_number", picklistNo);
        body = formData;
      }

      const response = await fetch(`${config.API_URL}/get_view_picklist`, {
        method: "POST",
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch Picker List");
      }

      const json = await response.json();
      const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
      if (data.data.reference_no.length == 1) {
        setShowCustomer(true);
      }
      const referenceNos = data.data.reference_no
        .map((item) => item.reference_no)
        .join(", ");
      const acNos = data.data.reference_no
        .map((item) => item.accound_no)
        .join(", ");
      const rnNos = data.data.reference_no
        .map((item) => item.route_number)
        .join(", ");

      setHeaderData({
        customerName: data.data.reference_no[0].customer,
        orderNumber: referenceNos,
        accountNumber: acNos,
        routeNumber: rnNos,
        pickingList: data.data.picklist_number,
        deliveryDate: data.data.reference_no[0].deliverydate,
        printBy: data.data.reference_no[0].print_by,
        printDate: data.data.reference_no[0].print_on,
        shop_name: data.data.shop_details.shop_name,
        address: data.data.shop_details.address,
        phone: data.data.shop_details.phone,
        email: data.data.shop_details.email,
        website: data.data.shop_details.website,
      });
      setPickList(data.data.packing_details);
      setPickComment(data.data.accound_no);

      const tQty = data.data.packing_details.reduce(
        (acc, product) => acc + (parseInt(product.quantity, 10) || 0),
        0
      );
      setTotalQty(tQty);
    } catch (error) {
      console.error("Error fetching Picker List data:", error);
    }
  };

  const changeQuantity = (e, code) => {
    const value = e.target.value;
    setPickList((prevPickList) => {
      const updatedPickList = prevPickList.map((item) =>
        item.code === code ? { ...item, quantity: value } : item
      );

      const total = updatedPickList.reduce(
        (acc, item) => acc + (parseInt(String(item.quantity || 0), 10) || 0),
        0
      );

      setTotalQty(total);
      return updatedPickList;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("picklist", JSON.stringify(pickList));
    formData.append("picklist_number", picklistNo);

    const apiUrl = `${config.API_URL}/ws-single-picking-submit`;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (response.ok) {
        Swal.fire({
          title: "Success!",
          text: "Picking completed successfully!.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          window.location.href = config.BASE_URL + `/picking/picking_list`;
        });
      } else {
        Swal.fire("error", "Picking not completed successfully!", "error");
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while submitting data.",
      });
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    fetchViewPickerList();
  }, []);

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid rgba(145, 158, 171, 0.16)",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 1.5, sm: 0 },
            }}
          >
            <Typography className="page-title">
              Picking List: {picklistNo}
            </Typography>
            <Button
              variant="contained"
              className="button-variant-contained"
              startIcon={<Iconify icon="eva:printer-fill" width={18} height={18} />}
              onClick={handleGeneratePickerListPDF}
              sx={{
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Print Picking List
            </Button>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  mb: { xs: 2, sm: 3 },
                  pb: { xs: 2, sm: 3 },
                  borderBottom: "1px solid rgba(145, 158, 171, 0.16)",
                }}
              >
                <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                  <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "center", sm: "left" } }}>
                    <Box
                      component="img"
                      src={`${config.BASE_URL}/assets/img/${accessToken}_logo.png`}
                      alt="Logo"
                      sx={{
                        width:
                          accessToken === "knm" || accessToken === "eve" || accessToken === "ve"
                            ? { xs: "40%", sm: "30%" }
                            : "auto",
                        maxWidth: "200px",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      {headerData.shop_name}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#637381",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        mb: 0.5,
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      {headerData.address}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#637381",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      <strong>Phone No:</strong> {headerData.phone} | <strong>Email:</strong>{" "}
                      {headerData.email}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#637381",
                        fontSize: { xs: "0.7rem", sm: "0.75rem" },
                        textAlign: { xs: "center", sm: "left" },
                      }}
                    >
                      <strong>Website:</strong> {headerData.website}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 0.5 } }}>
                      {showCustomer && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 0.25, sm: 0.5 },
                            alignItems: { xs: "flex-start", sm: "center" },
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#212b36",
                              mb: 0.5,
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            }}
                          >
                            Customer Name:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#637381",
                              fontSize: { xs: "0.7rem", sm: "0.875rem" },
                              wordBreak: "break-word",
                            }}
                          >
                            {headerData.customerName}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Order Number:
                        </Typography>
                        <Typography
                          sx={{
                            color: "#637381",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {headerData.orderNumber}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Account Number:
                        </Typography>
                        <Typography
                          sx={{
                            color: "#637381",
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            wordBreak: "break-word",
                          }}
                        >
                          {headerData.accountNumber}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Route Number:
                        </Typography>
                        <Typography sx={{ color: "#637381", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          {headerData.routeNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.75, sm: 0.5 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Picking List:
                        </Typography>
                        <Typography
                          sx={{
                            color: "#637381",
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          }}
                        >
                          {headerData.pickingList}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Delivery Date:
                        </Typography>
                        <Typography sx={{ color: "#637381", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          {formatNewDate(headerData.deliveryDate)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 600, color: "#212b36", fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                        >
                          Print By:
                        </Typography>
                        <Typography sx={{ color: "#637381", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          {firstName} {lastName}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: { xs: 0.25, sm: 0.5 },
                          alignItems: { xs: "flex-start", sm: "center" },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.75rem", sm: "0.8rem" },
                            color: "#212b36",
                          }}
                        >
                          Print Date:
                        </Typography>
                        <Typography sx={{ color: "#637381", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
                          {formatDate(todayDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <TableContainer>
                  <Table size="small" className="table-cell-border">
                    <TableHead>
                      <TableRow className="table-cell-header">
                        <TableCell sx={{ width: '100px' }}>Product Code</TableCell>
                        <TableCell sx={{ width: '300px' }}>Product Name</TableCell>
                        <TableCell sx={{ width: '80px' }}>Product Type</TableCell>
                        <TableCell align="right" sx={{ width: '100px' }}>Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pickList.map((item, index) => {
                        const isDiscount100 = parseFloat(item.discount || 0) === 100;
                        const qtyStr =
                          item.quantity === undefined || item.quantity === null
                            ? ""
                            : Number(item.quantity);
                        return (
                          <TableRow key={item.code || index} className="table-cell-style">
                            <TableCell sx={{ color: isDiscount100 ? "#f14240" : "#212b36" }}>
                              {item.code}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: isDiscount100 ? "#f14240" : "#212b36",
                                wordBreak: "break-word",
                              }}
                            >
                              {item.name}
                            </TableCell>
                            <TableCell sx={{ color: isDiscount100 ? "#f14240" : "#212b36" }}>
                              {item.order_type}
                            </TableCell>
                            <TableCell align="right" sx={{ color: isDiscount100 ? "#f14240" : "#212b36" }}>
                              <TextField
                                className="input-border"
                                name="name[]"
                                type={isDiscount100 ? "text" : "number"}
                                size="small"
                                inputProps={{
                                  min: 0,
                                  style: { textAlign: "right" },
                                }}
                                value={qtyStr}
                                onChange={(e) => changeQuantity(e, item.code)}
                                sx={{
                                  maxWidth: 120,
                                  "& .MuiOutlinedInput-root": { fontSize: { xs: "0.75rem", sm: "0.875rem" } },
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={2} />
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            color: "#212b36",
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.7rem", sm: "0.75rem" },
                            color: "#212b36",
                          }}
                        >
                          {totalQty}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </TableContainer>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: { xs: 2, sm: 3 } }}>
                <Button type="submit" variant="contained" className="button-variant-contained">
                  Submit
                </Button>
              </Box>

              {pickComment && pickComment.length > 0 && (
                <Box>
                  <TableContainer>
                    <Table size="small" className="table-cell-border">
                      <TableHead>
                        <TableRow className="table-cell-header">
                          <TableCell>A/c No</TableCell>
                          <TableCell>Comment</TableCell>
                          <TableCell>Comment By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pickComment.map((item) => {
                          const rows = [];
                          if (item.staff_note) {
                            rows.push(
                              <TableRow key={`${item.id}-staff-note`} className="table-cell-style">
                                <TableCell sx={{ color: "#212b36" }}>{item.accound_no}</TableCell>
                                <TableCell sx={{ color: "#212b36", wordBreak: "break-word" }}>
                                  {item.staff_note}
                                </TableCell>
                                <TableCell sx={{ color: "#212b36" }}>{item.name}</TableCell>
                              </TableRow>
                            );
                          }
                          if (item.note) {
                            rows.push(
                              <TableRow key={`${item.id}-note`} className="table-cell-style">
                                <TableCell sx={{ color: "#212b36" }}>{item.accound_no}</TableCell>
                                <TableCell sx={{ color: "#212b36", wordBreak: "break-word" }}>
                                  {item.note}
                                </TableCell>
                                <TableCell sx={{ color: "#212b36" }}>{item.name}</TableCell>
                              </TableRow>
                            );
                          }
                          return rows;
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default SingleViewPickinglist;
